import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SideBarModal } from '@/components';
import { Button } from '@/components/Button';
import { SearchableDropdown } from '@/components/SearchableDropdown';
import { DatePicker } from '@/components/DatePicker';
import { Vehicle } from '@/services/api/vehicles';
import { getListModel } from '@/services/api/models';
import { getListOem } from '@/services/api/oems';
import { getListPitstop, Pitstop } from '@/services/api/pitstops';
import { getPurchaseOrders } from '@/services/api/purchaseOrders';
import {
  COMPANY_ENTITY_OPTIONS,
  PurchaseOrder,
  PurchaseOrderStatus,
} from '@/types/purchase-order';
import { BUSINESS_UNIT_OPTIONS } from '@/types/common';
import {
  openImageFileDialog,
  getBucketUrlImageV2,
  getBucketUrlFile,
  uploadFileImage,
  uploadTempFile,
  fileToBase64,
  extractApiErrorMessage,
} from '@/utils';
import { showToast } from '@/components/toast/globalToast';
import { v4 as uuidv4 } from 'uuid';
import VehicleDetailView from './VehicleDetailView';

interface AddVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (vehicleData: any) => void;
  vehicle?: Vehicle | null;
  models?: any[];
  oems?: any[];
  requirePhotoAndEquipment?: boolean;
}

interface FormData {
  hubID: number;
  hubName: string;
  hubCityID?: number;
  hubCityName?: string;
  businessUnit: string;
  entity: string;
  platNomor: string;
  vin: string;
  imei: string;
  oemID: string;
  modelID: string;
  color: string;
  status: string;
  stnkAnnualExpiryDate: string;
  stnkAnnualFileUrl: string;
  stnkFiveYearExpiryDate: string;
  stnkFiveYearFileUrl: string;
  appUsername: string;
  appPassword: string;
  unitEquipment: string[];
  vehicleImageURL: string;
  contractDuration: string;
  contractStartDate: string;
  contractEndDate?: string;
  arriveAt: string;
  purchaseOrderID?: string;
  notes: string;
}

interface ValidationErrors {
  [key: string]: string | undefined;
}

const unitEquipmentOptions = [
  'Kaca Spion Kiri',
  'Kaca Spion Kanan',
  'Kunci Utama',
  'Kunci Cadangan',
  'Remote Utama',
  'Remote Cadangan',
  'STNK',
  'Plat Nomor Depan',
  'Plat Nomor Belakang',
  'Battery',
  'Other',
];

const CheckCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-green-500"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-gray-500 hover:text-red-500"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const AddVehicleModal: React.FC<AddVehicleModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  vehicle,
  models,
  oems,
  requirePhotoAndEquipment = false,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    hubID: 0,
    hubName: '',
    hubCityID: 0,
    hubCityName: '',
    businessUnit: '',
    entity: '',
    platNomor: '',
    vin: '',
    imei: '',
    oemID: '',
    modelID: '',
    color: '',
    status: 'IDLE',
    stnkAnnualExpiryDate: '',
    stnkAnnualFileUrl: '',
    stnkFiveYearExpiryDate: '',
    stnkFiveYearFileUrl: '',
    appUsername: '',
    appPassword: '',
    unitEquipment: [],
    vehicleImageURL: '',
    contractDuration: '',
    contractStartDate: '',
    contractEndDate: '',
    arriveAt: '',
    purchaseOrderID: '',
    notes: '',
  });
  const isEditMode = !!vehicle;
  // Free-text justification for an Entity / Business Unit correction. Kept out
  // of formData because it is not a vehicle field — it only rides along on the
  // update request and lands in the vehicle change log.
  const [changesReason, setChangesReason] = useState('');

  const [hubs, setHubs] = useState<Pitstop[]>([]);
  const [loadingHubs, setLoadingHubs] = useState(false);

  const fetchHubs = useCallback(async () => {
    try {
      setLoadingHubs(true);
      const response = await getListPitstop({
        page: '1',
        size: '10000',
        type: 'HUB',
        hasStaff: true,
      });
      setHubs(response.data || []);
    } catch (error) {
      showToast('error', 'Error', 'Failed to load hub locations');
      setHubs([]);
    } finally {
      setLoadingHubs(false);
    }
  }, []);

  // Purchase Orders — server-side searchable dropdown
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [poOptions, setPoOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [loadingPOs, setLoadingPOs] = useState(false);
  const poSearchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const fetchPurchaseOrders = useCallback(async (search?: string) => {
    try {
      setLoadingPOs(true);
      const response = await getPurchaseOrders({
        page: 1,
        size: 100,
        statuses: [
          PurchaseOrderStatus.APPROVED,
          PurchaseOrderStatus.RECEIVING,
          PurchaseOrderStatus.CLOSED,
        ],
        ...(search ? { search } : {}),
      });
      const data = response.data || [];
      setPurchaseOrders(data);
      setPoOptions(
        data.map((po) => ({
          value: (po.id || po.ID || '').toString(),
          label: po.code,
        }))
      );
    } catch (error) {
      setPurchaseOrders([]);
      setPoOptions([]);
    } finally {
      setLoadingPOs(false);
    }
  }, []);

  // Debounced search; only hits the API at >= 3 chars, resets to first 100 when cleared
  const handlePurchaseOrderSearch = useCallback(
    (query: string) => {
      if (poSearchDebounceRef.current) {
        clearTimeout(poSearchDebounceRef.current);
      }
      const trimmed = query.trim();
      poSearchDebounceRef.current = setTimeout(() => {
        if (trimmed.length >= 3) {
          fetchPurchaseOrders(trimmed);
        } else if (trimmed.length === 0) {
          fetchPurchaseOrders();
        }
      }, 300);
    },
    [fetchPurchaseOrders]
  );

  useEffect(() => {
    return () => {
      if (poSearchDebounceRef.current) {
        clearTimeout(poSearchDebounceRef.current);
      }
    };
  }, []);

  const [filteredModels, setFilteredModels] = useState<any[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [localModels, setLocalModels] = useState<any[]>([]);
  const [localOems, setLocalOems] = useState<any[]>([]);
  const [showDetailView, setShowDetailView] = useState(isEditMode && !!vehicle);
  const [detailTab, setDetailTab] = useState<'details' | 'equipment'>(
    'details'
  );

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [thumbnail, setThumbnail] = useState<string>('');

  const [tempThumbnail, setTempThumbnail] = useState<{
    name: string;
    base64: string;
    size?: number;
  } | null>(null);

  const [stnkFile, setStnkFile] = useState<{
    name: string;
    size: number;
  } | null>(null);
  const [isStnkUploading, setIsStnkUploading] = useState(false);
  const stnkFileInputRef = useRef<HTMLInputElement>(null);

  const [newOtherItem, setNewOtherItem] = useState<string>('');
  const [showOtherInput, setShowOtherInput] = useState<boolean>(false);

  const effectiveModels = models && models.length > 0 ? models : localModels;
  const effectiveOems = oems && oems.length > 0 ? oems : localOems;

  useEffect(() => {
    // Delay slightly to ensure React has finished rendering new step content
    const timeoutId = setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }, 10);
    return () => clearTimeout(timeoutId);
  }, [currentStep, showDetailView]);

  useEffect(() => {
    if (vehicle && isOpen) {
      // Find the OEM ID for the selected model
      const selectedModel = effectiveModels.find(
        (model) => model.ID === vehicle.modelID
      );
      const oemID = selectedModel?.oemID || '';

      const sharedStnkFileUrl =
        vehicle.stnkAnnualFileUrl || vehicle.stnkFiveYearFileUrl;
      setStnkFile(
        sharedStnkFileUrl
          ? {
              name: sharedStnkFileUrl.split('/').pop() || 'STNK.pdf',
              size: 0,
            }
          : null
      );

      setFormData({
        hubID: Number((vehicle as any).hubID) || 0,
        hubName: (vehicle as any).hubName || '',
        hubCityID: (vehicle as any).hubCityID || 0,
        hubCityName: (vehicle as any).hubCityName || '',
        businessUnit: (vehicle as any).businessUnit || '',
        entity: (vehicle as any).entity || '',
        platNomor: vehicle.platNomor || '',
        vin: vehicle.vin || '',
        imei: vehicle.imei || '',
        oemID: oemID,
        modelID: vehicle.modelID || '',
        color: vehicle.color || '',
        status: vehicle.status || 'IDLE',
        stnkAnnualExpiryDate: vehicle.stnkAnnualExpiryDate || '',
        stnkAnnualFileUrl: vehicle.stnkAnnualFileUrl || '',
        stnkFiveYearExpiryDate: vehicle.stnkFiveYearExpiryDate || '',
        stnkFiveYearFileUrl: vehicle.stnkFiveYearFileUrl || '',
        appUsername: vehicle.appUsername || '',
        appPassword: vehicle.appPassword || '',
        unitEquipment: vehicle.unitEquipment || [],
        vehicleImageURL: vehicle.vehicleImageURL || '',
        contractDuration: vehicle.contractDuration?.toString() || '',
        contractStartDate: vehicle.contractStartDate || '',
        arriveAt: vehicle.arriveAt || '',
        purchaseOrderID: (vehicle as any).purchaseOrderID || '',
        notes: vehicle.notes || '',
      });

      // Check if there are existing custom "Other - ..." items
      const hasCustomOtherItems = (vehicle.unitEquipment || []).some(
        (item: string) => item.startsWith('Other - ')
      );
      setShowOtherInput(hasCustomOtherItems);

      // Load models for the selected OEM when editing
      if (oemID) {
        loadModelsForOEM(oemID);
      }

      if (vehicle.vehicleImageURL) {
        setThumbnail(vehicle.vehicleImageURL);
      }
    } else if (isOpen) {
      setFormData({
        hubID: 0,
        hubName: '',
        hubCityID: 0,
        hubCityName: '',
        businessUnit: '',
        entity: '',
        platNomor: '',
        vin: '',
        imei: '',
        oemID: '',
        modelID: '',
        color: '',
        status: 'IDLE',
        contractDuration: '',
        contractStartDate: '',
        arriveAt: '',
        stnkAnnualExpiryDate: '',
        stnkAnnualFileUrl: '',
        stnkFiveYearExpiryDate: '',
        stnkFiveYearFileUrl: '',
        appUsername: '',
        appPassword: '',
        unitEquipment: [],
        vehicleImageURL: '',
        notes: '',
      });
      setTempThumbnail(null);
      setThumbnail('');
      setStnkFile(null);
      setShowOtherInput(false);
    }
    setErrors({});
    setCurrentStep(1);
    setFilteredModels([]);
  }, [vehicle, isOpen, effectiveModels]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await getListModel({ page: '1', size: '1000' });
        setLocalModels(response.data || []);
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to fetch models'
        );
        showToast('error', 'Error', errorMessage);
      }
    };

    const fetchOems = async () => {
      try {
        const response = await getListOem({
          page: '1',
          size: '1000',
          isActive: 'true',
        });
        setLocalOems(response.data || []);
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to fetch OEMs'
        );
        showToast('error', 'Error', errorMessage);
      }
    };

    if (!isOpen) return;
    fetchHubs();
    fetchPurchaseOrders();
    if (!oems || oems.length === 0) {
      fetchOems();
    }
    if (!models || models.length === 0) {
      fetchModels();
    }
  }, [isOpen, models, oems, fetchHubs, fetchPurchaseOrders]);

  const handleInputChange = (
    field: keyof FormData,
    value: string | string[] | number
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEquipmentToggle = (equipment: string) => {
    if (equipment === 'Other') {
      // Toggle the input field visibility
      setShowOtherInput(!showOtherInput);
      return;
    }

    const updatedEquipment = formData.unitEquipment.includes(equipment)
      ? formData.unitEquipment.filter((item) => item !== equipment)
      : [...formData.unitEquipment, equipment];

    handleInputChange('unitEquipment', updatedEquipment);
  };

  const addCustomOther = () => {
    if (
      newOtherItem.trim() &&
      !formData.unitEquipment.includes(`Other - ${newOtherItem.trim()}`)
    ) {
      const customOtherItem = `Other - ${newOtherItem.trim()}`;
      const updatedEquipment = [...formData.unitEquipment, customOtherItem];
      handleInputChange('unitEquipment', updatedEquipment);
      setNewOtherItem('');
    }
  };

  const removeCustomOther = (item: string) => {
    const updatedEquipment = formData.unitEquipment.filter(
      (equipment) => equipment !== item
    );
    handleInputChange('unitEquipment', updatedEquipment);
  };

  const loadModelsForOEM = async (oemId: string) => {
    if (oemId) {
      setLoadingModels(true);
      try {
        const response = await getListModel({
          page: '1',
          size: '1000',
          oemIDs: oemId,
        });
        setFilteredModels(response.data || []);
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to fetch models'
        );
        showToast('error', 'Error', errorMessage);
        setFilteredModels([]);
      } finally {
        setLoadingModels(false);
      }
    } else {
      setFilteredModels([]);
    }
  };

  const handleOEMChange = async (oemId: string) => {
    handleInputChange('oemID', oemId);
    handleInputChange('modelID', ''); // Reset model selection
    await loadModelsForOEM(oemId);
  };

  // Selecting a PO auto-fills OEM, Model, Entity & Business Unit from the PO.
  // Changing/clearing the PO resets those fields so they never go stale.
  const handlePurchaseOrderChange = (value: string) => {
    const selectedPO = purchaseOrders.find(
      (po) => (po.id || po.ID || '').toString() === value
    );

    if (selectedPO) {
      setFormData((prev) => ({
        ...prev,
        purchaseOrderID: value,
        oemID: selectedPO.oemID || '',
        modelID: selectedPO.modelID || '',
        businessUnit: selectedPO.requestingBusinessUnit || '',
        entity: selectedPO.entity || '',
      }));
      setErrors((prev) => ({
        ...prev,
        oemID: undefined,
        modelID: undefined,
        businessUnit: undefined,
        entity: undefined,
      }));
      loadModelsForOEM(selectedPO.oemID || '');
    } else {
      // Cleared (or not found) → wipe the auto-filled fields. Entity and
      // business unit survive on an existing unit: the API cannot carry a
      // blank for either, so wiping them would only strand the form.
      setFormData((prev) => ({
        ...prev,
        purchaseOrderID: '',
        oemID: '',
        modelID: '',
        businessUnit: isEditMode ? prev.businessUnit : '',
        entity: isEditMode ? prev.entity : '',
      }));
      setFilteredModels([]);
    }
  };

  const thumbnailUpload = async () => {
    openImageFileDialog(
      async (file: { name: string; base64: string; size: number } | null) => {
        try {
          setIsLoading(true);

          if (!file) {
            setIsLoading(false);
            return;
          }

          const uuid = uuidv4();

          const response = await getBucketUrlImageV2({
            type: 'vehicle',
            uuid: uuid,
          });

          if (!response?.data) {
            throw new Error('Failed to get upload URL');
          }

          const url = response.data;
          const base64Data = file?.base64?.split(',')[1];

          if (!base64Data) {
            throw new Error('Invalid file data');
          }

          // Set preview image
          const upload = await uploadFileImage(url, base64Data, file?.name);

          if (upload.status === 200) {
            setThumbnail(url);
            setTempThumbnail(file);
            handleInputChange('vehicleImageURL', url);
            showToast('success', 'Success', 'Upload image success');
          } else {
            throw new Error('Upload failed');
          }
        } catch (error: any) {
          const errorMessage = extractApiErrorMessage(
            error,
            'Failed to upload image'
          );
          showToast('error', 'Error', errorMessage);

          // Reset states on error
          setTempThumbnail(null);
        } finally {
          setIsLoading(false);
        }
      }
    );
  };

  const handleStnkFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.type !== 'application/pdf') {
      showToast('error', 'Error', 'Only PDF files are allowed');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showToast('error', 'Error', 'File size must be under 10 MB');
      return;
    }

    try {
      setIsStnkUploading(true);
      // Upload to the shared temp bucket like every other file/doc upload;
      // the backend moves it into the vehicle-scoped main bucket on create.
      const uuid = uuidv4();
      const bucketRes = await getBucketUrlFile({
        uuid,
        // The shared upload service only accepts a fixed set of file types
        // (thumbnail, vehicle, vehicleHandover, ...); the STNK PDF is a
        // vehicle document, so it rides in under 'vehicle'.
        fileType: 'vehicle',
        fileFormat: 'pdf',
      });
      if (!bucketRes?.data) {
        throw new Error('Failed to get upload URL');
      }
      const url = bucketRes.data;
      const base64Data = await fileToBase64(file);
      const uploadRes = await uploadTempFile(
        url,
        base64Data,
        file.name,
        file.type
      );
      if (uploadRes.status !== 200) {
        throw new Error('Upload failed');
      }

      // A single STNK PDF covers both annual and 5-yearly documents.
      setFormData((prev) => ({
        ...prev,
        stnkAnnualFileUrl: url,
        stnkFiveYearFileUrl: url,
      }));
      setStnkFile({ name: file.name, size: file.size });
      if (errors.stnkFile) {
        setErrors((prev) => ({ ...prev, stnkFile: undefined }));
      }
      showToast('success', 'Success', 'Upload STNK PDF success');
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to upload STNK PDF'
      );
      showToast('error', 'Error', errorMessage);
    } finally {
      setIsStnkUploading(false);
    }
  };

  const handleRemoveStnkFile = () => {
    setStnkFile(null);
    setFormData((prev) => ({
      ...prev,
      stnkAnnualFileUrl: '',
      stnkFiveYearFileUrl: '',
    }));
  };

  const selectedModel = effectiveModels.find(
    (m) => String(m.ID) === String(formData.modelID)
  );
  const contractType = selectedModel?.contractType;
  const isRTO = contractType === 'RTO';

  // When a PO is selected, OEM / Model / Entity / Business Unit are derived
  // from it and locked.
  const isPOSelected = !!formData.purchaseOrderID;
  const poLockHint =
    'Terisi otomatis dari Purchase Order. Ubah atau clear Purchase Order untuk menggantinya.';

  // Entity / Business Unit are inherited from the PO while adding a unit, but
  // an existing unit may need them corrected. Editing them is allowed and
  // recorded in the vehicle change log, so a reason is mandatory.
  const isPOInheritedLocked = isPOSelected && !isEditMode;
  // Clearing either field is not a change the API can carry (both are omitted
  // from the payload when blank), so an emptied field asks for no reason.
  const isCorrected = (field: 'entity' | 'businessUnit') =>
    isEditMode &&
    formData[field] !== '' &&
    formData[field] !== (vehicle?.[field] || '');
  const entityChanged = isCorrected('entity');
  const businessUnitChanged = isCorrected('businessUnit');
  const requiresChangesReason = entityChanged || businessUnitChanged;

  const validateStep1 = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.hubID) {
      newErrors.hubID = 'Hub Location is required';
    }
    if (!formData.businessUnit) {
      newErrors.businessUnit = 'Business Unit is required';
    }
    if (!formData.platNomor.trim()) {
      newErrors.platNomor = 'Plate Number is required';
    }
    if (!formData.vin.trim()) {
      newErrors.vin = 'VIN is required';
    }
    if (!formData.oemID) {
      newErrors.oemID = 'OEM is required';
    }
    if (!formData.modelID) {
      newErrors.modelID = 'Model is required';
    }
    if (!formData.color.trim()) {
      newErrors.color = 'Color is required';
    }
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }
    if (!formData.appUsername.trim()) {
      newErrors.appUsername = 'App Username is required';
    }
    if (!formData.appPassword.trim()) {
      newErrors.appPassword = 'App Password is required';
    }
    // STNK document + expiry dates are optional: some older vehicles have lost
    // or never had their STNK, so we let them through without it.

    // Contract validations
    if (isRTO) {
      const contractDurationNum = parseInt(formData.contractDuration);
      if (
        !formData.contractDuration.trim() ||
        isNaN(contractDurationNum) ||
        contractDurationNum <= 0
      ) {
        newErrors.contractDuration =
          'Contract Duration is required and must be greater than 0';
      }
    }

    if (!formData.contractStartDate.trim()) {
      newErrors.contractStartDate = 'Contract Start Date is required';
    }

    if (!formData.arriveAt.trim()) {
      newErrors.arriveAt = 'Tanggal & Waktu Kedatangan is required';
    }

    if (requiresChangesReason && !changesReason.trim()) {
      newErrors.vehicleChangesReason =
        'Alasan wajib diisi saat mengubah Entity atau Business Unit';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleSubmit = async () => {
    // Step 2 is optional, so there is nothing to validate here.
    setIsSubmitting(true);

    try {
      const contractDurationValue = isRTO
        ? parseInt(formData.contractDuration) || 0
        : undefined;
      const submitData = {
        ...formData,
        contractDuration: contractDurationValue,
        imei: formData.imei.trim() || undefined,
        purchaseOrderID: formData.purchaseOrderID?.trim() || undefined,
        entity: formData.entity?.trim() || undefined,
        stnkAnnualExpiryDate:
          formData.stnkAnnualExpiryDate?.trim() || undefined,
        stnkAnnualFileUrl: formData.stnkAnnualFileUrl?.trim() || undefined,
        stnkFiveYearExpiryDate:
          formData.stnkFiveYearExpiryDate?.trim() || undefined,
        stnkFiveYearFileUrl: formData.stnkFiveYearFileUrl?.trim() || undefined,
        vehicleChangesReason: requiresChangesReason
          ? changesReason.trim()
          : undefined,
      };

      await onSubmit(submitData);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
      setShowOtherInput(false);
    }
  };

  const handleClose = () => {
    setFormData({
      hubID: 0,
      hubName: '',
      hubCityID: 0,
      hubCityName: '',
      businessUnit: '',
      entity: '',
      platNomor: '',
      vin: '',
      imei: '',
      oemID: '',
      modelID: '',
      color: '',
      contractDuration: '',
      contractStartDate: '',
      arriveAt: '',
      status: 'IDLE',
      stnkAnnualExpiryDate: '',
      stnkAnnualFileUrl: '',
      stnkFiveYearExpiryDate: '',
      stnkFiveYearFileUrl: '',
      appUsername: '',
      appPassword: '',
      unitEquipment: [],
      vehicleImageURL: '',
      notes: '',
    });
    setErrors({});
    setCurrentStep(1);
    setTempThumbnail(null);
    setThumbnail('');
    setStnkFile(null);
    setFilteredModels([]);
    setNewOtherItem('');
    setShowOtherInput(false);
    onClose();
  };

  // Keyed on isOpen alone: a re-rendered parent handing over a new `vehicle`
  // object must not wipe a reason the user is halfway through typing.
  useEffect(() => {
    setChangesReason('');
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && isEditMode && vehicle) {
      setShowDetailView(true);
      setDetailTab('details'); // optional: reset to details tab
    }
    if (isOpen && (!isEditMode || !vehicle)) {
      setShowDetailView(false);
      setDetailTab('details');
    }
  }, [isOpen, isEditMode, vehicle]);

  const isStep1Valid =
    formData.hubID !== 0 &&
    formData.businessUnit !== '' &&
    formData.platNomor.trim() !== '' &&
    formData.vin.trim() !== '' &&
    formData.oemID !== '' &&
    formData.modelID !== '' &&
    formData.color.trim() !== '' &&
    formData.status !== '' &&
    formData.appUsername.trim() !== '' &&
    formData.appPassword.trim() !== '' &&
    (!isRTO ||
      (formData.contractDuration.trim() !== '' &&
        parseInt(formData.contractDuration) > 0)) &&
    formData.contractStartDate.trim() !== '' &&
    (!requiresChangesReason || changesReason.trim() !== '');

  const isStep2Valid =
    !requirePhotoAndEquipment ||
    (formData.vehicleImageURL !== '' && formData.unitEquipment.length > 0);

  if (!isOpen) return null;

  return (
    <>
      <SideBarModal
        isOpen={isOpen}
        onClose={handleClose}
        position="right"
        width="sm"
        contentRef={scrollContainerRef}
        header={
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {showDetailView
                ? 'Vehicle Details'
                : isEditMode
                  ? 'Edit Owned Vehicle'
                  : 'Add Owned Vehicle'}
            </h2>
            <p className="text-sm font-normal text-gray-600">
              {showDetailView
                ? 'Review vehicle information'
                : isEditMode
                  ? 'Edit Owned vehicle details.'
                  : 'New Owned vehicle details.'}
            </p>
          </div>
        }
        footer={
          showDetailView ? (
            <div className="flex flex-row gap-3">
              <Button
                variant="primary"
                className="w-full py-3"
                onClick={() => setShowDetailView(false)}
              >
                Edit
              </Button>
            </div>
          ) : currentStep === 1 ? (
            <Button
              variant="primary"
              onClick={handleNext}
              className="w-full py-3"
              disabled={!isStep1Valid}
            >
              Next
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setCurrentStep(1)}
                className="flex-1 py-3"
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || !isStep2Valid}
                className="flex-1 py-3"
              >
                {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
              </Button>
            </div>
          )
        }
      >
        {showDetailView ? (
          <VehicleDetailView
            detailTab={detailTab}
            formData={{
              ...formData,
              contractDuration: parseInt(formData.contractDuration) || 0,
            }}
            initialData={vehicle}
            vehicleEquipment={formData.unitEquipment}
            onTabChange={setDetailTab}
          />
        ) : (
          <>
            {/* Step Navigation */}
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center">
                <div className={`flex items-center`}>
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-medium ${
                      currentStep > 1
                        ? 'bg-green-600 text-white border-green-600'
                        : currentStep >= 1
                          ? 'bg-[#5E2AAC] text-white border-[#5E2AAC]'
                          : 'border-gray-300'
                    }`}
                  >
                    {currentStep > 1 ? (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      '1'
                    )}
                  </div>
                  <span className="ml-2 text-sm font-medium">
                    Vehicle Details
                  </span>
                </div>

                <div className="flex-1 mx-4">
                  <div className={`h-0.5 ${'bg-gray-200'}`}></div>
                </div>

                <div className={`flex items-center `}>
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-medium ${
                      currentStep >= 2
                        ? 'bg-[#5E2AAC] text-white border-[#5E2AAC]'
                        : 'border-gray-300'
                    }`}
                  >
                    2
                  </div>
                  <span className="ml-2 text-sm font-medium">
                    Equipment Checklist
                  </span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              {currentStep === 1 ? (
                <div className="pb-8">
                  <div className="space-y-4">
                    <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-400 rounded-md">
                      Vehicle Detail
                    </div>

                    <div className="px-6 space-y-4">
                      {/* Purchase Order ID (optional, server-side searchable) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Purchase Order ID
                        </label>
                        <SearchableDropdown
                          placeholder="Search Purchase Order ID..."
                          options={poOptions}
                          value={formData.purchaseOrderID || ''}
                          selectedLabelFallback={
                            (vehicle as any)?.purchaseOrderCode || ''
                          }
                          onChange={handlePurchaseOrderChange}
                          onSearchChange={handlePurchaseOrderSearch}
                          disableLocalFilter
                          loading={loadingPOs}
                        />
                      </div>

                      {/* Tanggal & Waktu Kedatangan */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tanggal & Waktu Kedatangan
                          <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                          value={formData.arriveAt}
                          onChange={(value: string) =>
                            handleInputChange('arriveAt', value)
                          }
                          placeholder="Select arrival date and time"
                          showTimeSelect={true}
                          error={!!errors.arriveAt}
                          className="w-full"
                        />
                        {errors.arriveAt && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.arriveAt}
                          </p>
                        )}
                      </div>

                      {/* Hub Location */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hub Location <span className="text-red-500">*</span>
                        </label>
                        <SearchableDropdown
                          placeholder={
                            loadingHubs
                              ? 'Loading hubs...'
                              : 'Select hub location'
                          }
                          options={hubs.map((hub) => ({
                            value:
                              hub.id?.toString() || hub.ID?.toString() || '',
                            label: hub.name || '',
                          }))}
                          value={
                            formData.hubID ? formData.hubID.toString() : ''
                          }
                          onChange={(value) => {
                            handleInputChange('hubID', Number(value));
                            const selectedHub = hubs.find(
                              (h) =>
                                (h.id?.toString() || h.ID?.toString()) === value
                            );
                            handleInputChange(
                              'hubName',
                              selectedHub?.name || ''
                            );
                            handleInputChange(
                              'hubCityID',
                              selectedHub?.city?.id || selectedHub?.cityID || 0
                            );
                            handleInputChange(
                              'hubCityName',
                              selectedHub?.city?.name ||
                                selectedHub?.cityName ||
                                ''
                            );
                          }}
                          disabled={loadingHubs}
                        />
                        {errors.hubID && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.hubID}
                          </p>
                        )}
                      </div>

                      {/* Entity — inherited from the PO while adding, editable
                          on an existing unit (audit-logged, needs a reason) */}
                      <div title={isPOInheritedLocked ? poLockHint : undefined}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Entity
                        </label>
                        <SearchableDropdown
                          placeholder="Select entity"
                          options={COMPANY_ENTITY_OPTIONS}
                          value={formData.entity}
                          onChange={(value) =>
                            handleInputChange('entity', value)
                          }
                          disabled={isPOInheritedLocked}
                        />
                        {isPOInheritedLocked && (
                          <p className="flex items-center text-xs text-gray-500 mt-1">
                            <i className="ri-lock-line mr-1 text-gray-400 text-sm" />
                            {poLockHint}
                          </p>
                        )}
                      </div>

                      {/* Business Unit */}
                      <div title={isPOInheritedLocked ? poLockHint : undefined}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Business Unit <span className="text-red-500">*</span>
                        </label>
                        <SearchableDropdown
                          placeholder="Select business unit"
                          options={BUSINESS_UNIT_OPTIONS}
                          value={formData.businessUnit}
                          onChange={(value) =>
                            handleInputChange('businessUnit', value)
                          }
                          disabled={isPOInheritedLocked}
                        />
                        {isPOInheritedLocked && (
                          <p className="flex items-center text-xs text-gray-500 mt-1">
                            <i className="ri-lock-line mr-1 text-gray-400 text-sm" />
                            {poLockHint}
                          </p>
                        )}
                        {errors.businessUnit && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.businessUnit}
                          </p>
                        )}
                      </div>

                      {/* Reason — only when Entity / Business Unit actually
                          changed on an existing unit */}
                      {requiresChangesReason && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Alasan Perubahan{' '}
                            {[
                              entityChanged && 'Entity',
                              businessUnitChanged && 'Business Unit',
                            ]
                              .filter(Boolean)
                              .join(' & ')}{' '}
                            <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            rows={3}
                            value={changesReason}
                            onChange={(e) => {
                              setChangesReason(e.target.value);
                              if (errors.vehicleChangesReason) {
                                setErrors((prev) => ({
                                  ...prev,
                                  vehicleChangesReason: undefined,
                                }));
                              }
                            }}
                            placeholder="Contoh: koreksi salah input saat receive PO-2024-011"
                            className={`w-full px-3 py-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.vehicleChangesReason
                                ? 'border-red-500'
                                : 'border-gray-300'
                            }`}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Tercatat di riwayat perubahan unit beserta nilai
                            sebelum dan sesudah.
                          </p>
                          {errors.vehicleChangesReason && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.vehicleChangesReason}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Plate Number */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Plate Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.platNomor}
                          onChange={(e) =>
                            handleInputChange('platNomor', e.target.value)
                          }
                          placeholder="e.g. B 1234 ABC"
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.platNomor
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                        />
                        {errors.platNomor && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.platNomor}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nomor Rangka / VIN{' '}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.vin}
                          onChange={(e) =>
                            handleInputChange('vin', e.target.value)
                          }
                          placeholder="e.g. MHFU48110C0004845"
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.vin ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.vin && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.vin}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nomor Mesin
                        </label>
                        <input
                          type="text"
                          value={formData.imei}
                          onChange={(e) =>
                            handleInputChange('imei', e.target.value)
                          }
                          placeholder="e.g. 147KW06792WD532G501"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Status <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.status}
                          onChange={(e) =>
                            handleInputChange('status', e.target.value)
                          }
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed ${
                            errors.status ? 'border-red-500' : 'border-gray-300'
                          }`}
                          disabled={true}
                        >
                          <option value="">Select Status</option>
                          <option value="IDLE">Idle</option>
                          <option value="ACTIVE">Active</option>
                          <option value="BROKEN">Broken</option>
                          <option value="IN_REPAIR">In Repair</option>

                          <option value="LOST">Lost</option>
                        </select>
                        {errors.status && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.status}
                          </p>
                        )}
                      </div>

                      <div title={isPOSelected ? poLockHint : undefined}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          OEM Name <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.oemID}
                          onChange={(e) => handleOEMChange(e.target.value)}
                          disabled={isPOSelected}
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                            errors.oemID ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select OEM</option>
                          {effectiveOems.map((oem) => (
                            <option key={oem.ID} value={oem.ID}>
                              {oem.name}
                            </option>
                          ))}
                        </select>
                        {isPOSelected && (
                          <p className="flex items-center text-xs text-gray-500 mt-1">
                            <i className="ri-lock-line mr-1 text-gray-400 text-sm" />
                            {poLockHint}
                          </p>
                        )}
                        {errors.oemID && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.oemID}
                          </p>
                        )}
                      </div>

                      <div title={isPOSelected ? poLockHint : undefined}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Model Name <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.modelID}
                          onChange={(e) =>
                            handleInputChange('modelID', e.target.value)
                          }
                          disabled={
                            isPOSelected || !formData.oemID || loadingModels
                          }
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed ${
                            errors.modelID
                              ? 'border-red-500'
                              : 'border-gray-300'
                          } ${
                            isPOSelected || !formData.oemID || loadingModels
                              ? 'bg-gray-100'
                              : ''
                          }`}
                        >
                          <option value="">
                            {loadingModels
                              ? 'Loading models...'
                              : !formData.oemID
                                ? 'Select OEM first'
                                : 'Select Model'}
                          </option>
                          {filteredModels.map((model) => (
                            <option key={model.ID} value={model.ID}>
                              {model.name}
                            </option>
                          ))}
                        </select>
                        {formData.modelID && contractType && (
                          <div className="flex items-center text-xs text-gray-500">
                            <i className="ri-information-fill mr-1 text-gray-400 text-base"></i>
                            Tipe kontrak ={' '}
                            {contractType === 'RTO' ? 'RTO' : 'Rent'}
                          </div>
                        )}
                        {errors.modelID && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.modelID}
                          </p>
                        )}
                        {isPOSelected && (
                          <p className="flex items-center text-xs text-gray-500 mt-1">
                            <i className="ri-lock-line mr-1 text-gray-400 text-sm" />
                            {poLockHint}
                          </p>
                        )}
                        {!isPOSelected && !formData.oemID && (
                          <p className="text-xs text-gray-500 mt-1">
                            Please select an OEM first to see available models
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Color <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.color}
                          onChange={(e) =>
                            handleInputChange('color', e.target.value)
                          }
                          placeholder="e.g. Red"
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.color ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.color && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.color}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          App Username <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.appUsername}
                          onChange={(e) =>
                            handleInputChange('appUsername', e.target.value)
                          }
                          placeholder="e.g. 02054006"
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.appUsername
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                        />
                        {errors.appUsername && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.appUsername}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          App Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.appPassword}
                          onChange={(e) =>
                            handleInputChange('appPassword', e.target.value)
                          }
                          placeholder="e.g. 8mH28qk04"
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.appPassword
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                        />
                        {errors.appPassword && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.appPassword}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Document Detail - STNK */}
                    <div>
                      <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 border-gray-200 heading-field">
                        Document Detail
                      </div>

                      <div className="px-6 mt-4 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            STNK (PDF)
                          </label>

                          <div className="border-2 border-dashed border-gray-300 rounded-lg w-full">
                            {isStnkUploading ? (
                              <div className="p-8 flex flex-col justify-center items-center text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                <p className="mt-2 text-sm text-gray-600">
                                  Uploading STNK PDF...
                                </p>
                              </div>
                            ) : !stnkFile ? (
                              <div className="p-8 flex flex-col justify-center items-center text-center">
                                <div className="text-gray-400 text-4xl mb-4">
                                  <i className="ri-file-pdf-2-line" />
                                </div>
                                <p className="font-semibold text-gray-700">
                                  Upload STNK document
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  PDF format, up to 10 MB.
                                </p>
                                <button
                                  onClick={() =>
                                    stnkFileInputRef.current?.click()
                                  }
                                  className="mt-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  Browse File
                                </button>
                              </div>
                            ) : (
                              <div className="p-4 flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="text-gray-600">
                                    <i className="ri-file-pdf-2-line text-2xl" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-gray-800">
                                      {stnkFile.name}
                                    </p>
                                    <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                                      {stnkFile.size > 0 && (
                                        <span>
                                          {formatBytes(stnkFile.size)}
                                        </span>
                                      )}
                                      <CheckCircleIcon />
                                      <span className="text-green-600 font-medium">
                                        Completed
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <button onClick={handleRemoveStnkFile}>
                                  <TrashIcon />
                                </button>
                              </div>
                            )}
                          </div>
                          <input
                            ref={stnkFileInputRef}
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={handleStnkFileSelect}
                          />
                          {errors.stnkFile && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.stnkFile}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Masa Berlaku Tahunan
                          </label>
                          <DatePicker
                            value={formData.stnkAnnualExpiryDate}
                            onChange={(value: string) =>
                              handleInputChange('stnkAnnualExpiryDate', value)
                            }
                            placeholder="Select expiry date"
                            showTimeSelect={false}
                            className="w-full"
                          />
                          {errors.stnkAnnualExpiryDate && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.stnkAnnualExpiryDate}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Masa Berlaku 5 Tahunan
                          </label>
                          <DatePicker
                            value={formData.stnkFiveYearExpiryDate}
                            onChange={(value: string) =>
                              handleInputChange(
                                'stnkFiveYearExpiryDate',
                                value
                              )
                            }
                            placeholder="Select expiry date"
                            showTimeSelect={false}
                            className="w-full"
                          />
                          {errors.stnkFiveYearExpiryDate && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.stnkFiveYearExpiryDate}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-400 rounded-md">
                      Contract Detail
                    </div>
                    <div className="px-6 space-y-4">
                      {isRTO && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contract Duration
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={formData.contractDuration}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow digits and prevent leading zeros (except empty string)
                                if (
                                  value === '' ||
                                  (/^\d+$/.test(value) &&
                                    value !== '0' &&
                                    !value.startsWith('0'))
                                ) {
                                  handleInputChange('contractDuration', value);
                                }
                              }}
                              placeholder="e.g. 12"
                              className={`w-full px-3 py-2 pr-16 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.contractDuration
                                  ? 'border-red-500'
                                  : 'border-gray-300'
                              }`}
                            />
                            <div
                              className={`absolute inset-y-0 right-0 flex border items-center p-3 pointer-events-none ${errors.contractDuration ? 'border-red-500' : 'border-gray-300'}`}
                            >
                              <span className={`text-gray-500 text-sm `}>
                                Months
                              </span>
                            </div>
                          </div>
                          {errors.contractDuration && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.contractDuration}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Contract
                          <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                          value={formData.contractStartDate}
                          onChange={(value: string) =>
                            handleInputChange('contractStartDate', value)
                          }
                          placeholder="Select contract start date"
                          showTimeSelect={false}
                          error={!!errors.contractStartDate}
                          className="w-full"
                        />
                        {errors.contractStartDate && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.contractStartDate}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="pb-8">
                  <div className="space-y-6">
                    {/* Image Upload Section */}
                    <div>
                      <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 border-gray-200 heading-field">
                        Additional Vehicle Detail
                      </div>

                      <div className="px-6 mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Foto Kendaraan{' '}
                          {requirePhotoAndEquipment ? (
                            <span className="text-red-500">*</span>
                          ) : (
                            <span className="text-gray-400 font-normal">
                              (opsional)
                            </span>
                          )}
                        </label>

                        <div className="border-2 border-dashed border-gray-300 rounded-lg w-full">
                          {isLoading ? (
                            <div className="p-8 flex flex-col justify-center items-center text-center">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                              <p className="mt-2 text-sm text-gray-600">
                                Uploading image...
                              </p>
                            </div>
                          ) : !tempThumbnail && !thumbnail ? (
                            // Initial State: Dropzone
                            <div className="p-8 flex flex-col justify-center items-center text-center">
                              <div className="text-gray-400 text-4xl mb-4">
                                <svg
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  className="w-12 h-12 mx-auto"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                              <p className="font-semibold text-gray-700">
                                Add a vehicle image
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                JPEG and PNG formats, up to 10 MB.
                              </p>
                              <button
                                onClick={() => {
                                  thumbnailUpload();
                                }}
                                className="mt-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                Browse File
                              </button>
                            </div>
                          ) : (
                            // Success State: File Info
                            <div className="p-4 flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="text-gray-600">
                                  <svg
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    className="w-8 h-8"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-800">
                                    {tempThumbnail?.name || 'Vehicle Image'}
                                  </p>
                                  <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                                    {!isEditMode && (
                                      <span>
                                        {formatBytes(tempThumbnail?.size || 0)}
                                      </span>
                                    )}
                                    <CheckCircleIcon />
                                    <span className="text-green-600 font-medium">
                                      Completed
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  setThumbnail('');
                                  setTempThumbnail(null);
                                  handleInputChange('vehicleImageURL', '');
                                }}
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Equipment Checklist */}
                    <div>
                      <div className="px-6 py-2 text-sm font-medium text-gray-700 rounded-md mb-2">
                        Kelengkapan Unit{' '}
                        {requirePhotoAndEquipment ? (
                          <span className="text-red-500">*</span>
                        ) : (
                          <span className="text-gray-400 font-normal">
                            (opsional)
                          </span>
                        )}
                      </div>

                      <div className="space-y-3 px-6">
                        {unitEquipmentOptions.map((equipment) => (
                          <label
                            key={equipment}
                            className="flex items-center cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={
                                equipment === 'Other'
                                  ? showOtherInput ||
                                    formData.unitEquipment.some((item) =>
                                      item.startsWith('Other - ')
                                    )
                                  : formData.unitEquipment.includes(equipment)
                              }
                              onChange={() => handleEquipmentToggle(equipment)}
                              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                              style={{ accentColor: '#5E2AAC' }}
                            />
                            <span className="ml-3 text-sm text-gray-700">
                              {equipment}
                            </span>
                          </label>
                        ))}

                        {/* Custom Other Items */}
                        {formData.unitEquipment
                          .filter((item) => item.startsWith('Other - '))
                          .map((item) => (
                            <div
                              key={item}
                              className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md"
                            >
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={true}
                                  readOnly
                                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                  style={{ accentColor: '#5E2AAC' }}
                                />
                                <span className="ml-3 text-sm text-gray-700">
                                  {item}
                                </span>
                              </label>
                              <button
                                onClick={() => removeCustomOther(item)}
                                className="text-gray-500 hover:text-gray-700 p-1"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          ))}

                        {/* Add Custom Other Input - Show when Other is checked and limit not reached */}
                        {showOtherInput &&
                          formData.unitEquipment.filter((item) =>
                            item.startsWith('Other - ')
                          ).length < 10 && (
                            <div className="mt-4">
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={newOtherItem}
                                  onChange={(e) =>
                                    setNewOtherItem(e.target.value)
                                  }
                                  placeholder="Enter item name"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      addCustomOther();
                                    }
                                  }}
                                />
                                <button
                                  onClick={addCustomOther}
                                  disabled={!newOtherItem.trim()}
                                  className="px-3 py-2 bg-[#5E2AAC] text-white rounded-md text-sm font-medium hover:bg-[#5E2AAC] disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                  + Add
                                </button>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Maximum 10 items allowed (
                                {
                                  formData.unitEquipment.filter((item) =>
                                    item.startsWith('Other - ')
                                  ).length
                                }
                                /10)
                              </p>
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="px-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) =>
                          handleInputChange('notes', e.target.value)
                        }
                        placeholder="e.g. Motor in bekas kecelakaan"
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                      <div className="text-right text-xs text-gray-500 mt-1">
                        {formData.notes.length}/200
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </SideBarModal>
    </>
  );
};

export default AddVehicleModal;
